import {
  Box,
  Card,
  CardContent,
  CardHeader,
  darken,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
  useTheme
} from '@mui/material'
import { Aurora, AuroraAwhpEvent, AuroraAwhpData, Sensor } from '../types'
import { useTranslation } from 'react-i18next'
import { AcUnit, OpenInNew, WbSunny } from '@mui/icons-material'
import AuroraStatusIndicator from './AuroraStatusIndicator'
import { formatPercentage, formatPower, formatTemperatureDelta } from '../utils/string'
import { cyan, orange } from '@mui/material/colors'
import { subMinutes } from 'date-fns'
import { calculateAuroraHeatOutput, calculateAuroraHeatOutputPercentage } from '../utils/aurora'
import { NavLink, useLocation, useMatch } from 'react-router-dom'
import { AuroraModel } from '../enums'
import { useEffect, useState } from 'react'
import AuroraBackdrop from './AuroraBackdrop'
import { getAuroraAwhpActiveEvents, getAuroraAwhpDataNow } from '../services/aurora'
import { getSensorNow } from '../services/deviceService'
import axios from 'axios'
import { createErrorNotification } from '../redux/slices/notifications'
import { useDispatch } from 'react-redux'

export interface AuroraBlockCardProps {
  aurora: Aurora
  auroraAwhpData?: AuroraAwhpData | null
  auroraAwhpActiveEvents?: AuroraAwhpEvent[]
  sensor?: Sensor | null
}

export default function AuroraBlockCard({
  aurora,
  auroraAwhpData: initialAuroraAwhpData,
  auroraAwhpActiveEvents: initialAuroraAwhpActiveEvents,
  sensor: initialSensor
}: AuroraBlockCardProps) {
  const { t } = useTranslation('aurora')
  const dispatch = useDispatch()

  const theme = useTheme()

  /**
   * Indicates if the user is already on the aurora page.
   */
  const isOnAuroraPage = useMatch(`/auroras/${aurora.id}`) !== null

  const location = useLocation()

  const [auroraAwhpData, setAuroraAwhpData] = useState<AuroraAwhpData | null>(
    initialAuroraAwhpData !== undefined ? initialAuroraAwhpData : null
  )

  const [auroraAwhpActiveEvents, setAuroraAwhpActiveEvents] = useState<AuroraAwhpEvent[]>(
    initialAuroraAwhpActiveEvents !== undefined ? initialAuroraAwhpActiveEvents : []
  )

  const [sensor, setSensor] = useState<Sensor | null>(
    initialSensor !== undefined ? initialSensor : null
  )

  /**
   * The heat output.
   */
  const heatOutput =
    auroraAwhpData !== null ? calculateAuroraHeatOutput(aurora, auroraAwhpData) : null

  /**
   * The heat output percentage.
   */
  const heatOutputPercentage =
    auroraAwhpData !== null ? calculateAuroraHeatOutputPercentage(aurora, auroraAwhpData) : null

  /**
   * The electric power.
   */
  const electricPower = auroraAwhpData !== null ? auroraAwhpData.electricity_active_power : null

  /**
   * The temperature delta.
   */
  const temperatureDelta =
    auroraAwhpData !== null
      ? auroraAwhpData.temperature_primary_supply - auroraAwhpData.temperature_primary_return
      : null

  /**
   * Indicates if the unit is online.
   */
  const isRunning =
    auroraAwhpData !== null && new Date(auroraAwhpData.time) > subMinutes(new Date(), 3)

  /**
   * Indicates if the unit is defrosting.
   */
  const isDefrosting = auroraAwhpActiveEvents.some((event) => event.key === 'DEFROST')

  /**
   * Indicates if the unit is cooling.
   */
  const isCooling = false

  /**
   * Indicates if the unit is heating.
   */
  const isHeating = false

  /**
   * Indicates if the aurora is backup power unit.
   */
  const isBackupPowerUnit = aurora?.catalogProduct?.name === AuroraModel.AURORA_64

  /**
   * The status.
   */
  const status = isDefrosting ? t('defrosting') : isRunning ? t('running') : t('offline')

  /**
   * The color of the status text.
   */
  const statusColor = isDefrosting ? 'info.light' : isRunning ? 'success.light' : 'error.light'

  const loadAuroraAwhpData = async (auroraId: string, controller: AbortController) => {
    try {
      setAuroraAwhpData(await getAuroraAwhpDataNow(auroraId, { signal: controller.signal }))
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      setAuroraAwhpData(null)
      dispatch(createErrorNotification('failedToLoadStatus'))
    }
  }

  const loadAuroraAwhpActiveEvents = async (auroraId: string, controller: AbortController) => {
    try {
      setAuroraAwhpActiveEvents(
        await getAuroraAwhpActiveEvents(auroraId, { signal: controller.signal })
      )
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      setAuroraAwhpActiveEvents([])
      console.error(error)
    }
  }

  const loadSensor = async (deviceId: string, controller: AbortController) => {
    try {
      setSensor(await getSensorNow(deviceId, { signal: controller.signal }))
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      setSensor(null)
      console.error(error)
    }
  }

  useEffect(() => {
    if (initialAuroraAwhpData !== undefined) {
      setAuroraAwhpData(initialAuroraAwhpData)
    } else {
      const controller = new AbortController()

      loadAuroraAwhpData(aurora.id, controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadAuroraAwhpData(aurora.id, controller)
      }, ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [aurora.id, initialAuroraAwhpData])

  useEffect(() => {
    if (initialAuroraAwhpActiveEvents !== undefined) {
      setAuroraAwhpActiveEvents(initialAuroraAwhpActiveEvents)
    } else {
      const controller = new AbortController()

      loadAuroraAwhpActiveEvents(aurora.id, controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadAuroraAwhpActiveEvents(aurora.id, controller)
      }, ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [aurora.id, initialAuroraAwhpActiveEvents])

  useEffect(() => {
    if (initialSensor !== undefined) {
      setSensor(initialSensor)
    } else if (aurora.deviceId === undefined || aurora.deviceId === null) {
      setSensor(null)
    } else {
      const controller = new AbortController()

      loadSensor(aurora.deviceId, controller)

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        loadSensor(aurora.deviceId!, controller)
      }, ONE_MINUTE)

      return () => {
        clearInterval(interval)
        controller.abort()
      }
    }
  }, [aurora.deviceId, initialSensor])

  return (
    <Box
      sx={{
        minWidth: 296,
        maxWidth: 296,
        width: '100%',
        aspectRatio: '3 / 4',
        background: darken(theme.palette.background.default, 0.01),
        borderRadius: 2,
        flexShrink: { xs: 1, sm: 0 }
      }}
    >
      <Card
        sx={{
          display: 'flex',
          flexFlow: 'column',
          position: 'relative',
          height: '100%'
        }}
      >
        <AuroraBackdrop aurora={aurora} />
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" gap={2}>
              <AuroraStatusIndicator
                aurora={aurora}
                auroraAwhpData={auroraAwhpData}
                sx={{ fontSize: 12 }}
              />
              <Typography variant="h6">{aurora?.asset?.name ?? aurora.id}</Typography>
            </Stack>
          }
          subheader={aurora.id}
          action={
            !isOnAuroraPage && (
              <Tooltip title={t('actions:showData')}>
                <IconButton
                  component={NavLink}
                  to={`/auroras/${aurora.id}`}
                  state={{ from: location.pathname }}
                >
                  <OpenInNew htmlColor={theme.palette.text.secondary} />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <CardContent sx={{ flexGrow: 1, py: 0, zIndex: 1 }}>
          <Stack direction="column" justifyContent="flex-end" gap={4} sx={{ height: '100%' }}>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <AuroraBlockCardStatsBox
                    label={t('heatOutput')}
                    value={formatPower(heatOutput, true, 1)}
                  />
                </Grid>

                <Grid item xs={6}>
                  <AuroraBlockCardStatsBox
                    label={t('electricPower')}
                    value={formatPower(electricPower, true, 1)}
                  />
                </Grid>

                <Grid item xs={6}>
                  <AuroraBlockCardStatsBox
                    label="ΔT"
                    value={formatTemperatureDelta(temperatureDelta, true, 1)}
                  />
                </Grid>

                <Grid item xs={6}>
                  <AuroraBlockCardStatsBox label={t('status')} value={status} color={statusColor} />
                </Grid>
              </Grid>
            </Box>

            <Box>
              {heatOutputPercentage !== null && (
                <Stack direction="row" gap={4} justifyContent="flex-start" alignItems="center">
                  <LinearProgress
                    variant="determinate"
                    value={heatOutputPercentage}
                    sx={{ width: '100%', height: 8, borderRadius: 8 }}
                  />
                  <Typography color="text.secondary">
                    {formatPercentage(heatOutputPercentage)}
                  </Typography>
                </Stack>
              )}

              <Stack direction="row" gap={4} mt={2}>
                {isCooling && (
                  <Tooltip title={t('cooling')}>
                    <AcUnit htmlColor={cyan[500]} />
                  </Tooltip>
                )}

                {isHeating && (
                  <Tooltip title={t('heating')}>
                    <WbSunny htmlColor={orange[500]} />
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

interface AuroraBlockCardStatsBoxProps {
  label: string
  value: string
  color?: TypographyProps['color']
}

function AuroraBlockCardStatsBox({
  label,
  value,
  color = 'text.primary'
}: AuroraBlockCardStatsBoxProps) {
  return (
    <Box>
      <Typography color="text.secondary">{label}</Typography>
      <Typography variant="h6" color={color}>
        {value}
      </Typography>
    </Box>
  )
}
